/**
 * simulateWbsSchedule.js — synthesize a plausible schedule from the WBS template so it can be drawn
 * with the same Gantt engine (gantt/GanttTimeline.jsx) real projects use.
 *
 * A WBS template has no calendar dates — only a touch-time/wait duration (`default_duration`) and a
 * predecessor graph (`predecessor_ids`). At real scaffold time every one whose phase exists on the
 * project is linked, and the milestone waits for the latest of them (Matt, 2026-09-23). This
 * simulation doesn't know which phases a real project will have, so it draws the fullest
 * plausible chain: every predecessor is treated as real, and a row starts the first workday after
 * the LATEST of all of them finishes.
 *
 * Forward topological pass, mirroring the shape of api/_scheduleTimeline.js's longest-path walk
 * (that one sums days backward from a target; this one assigns dates forward from an anchor).
 * Anchor is "today" — the same reference `ganttLayout.js#buildWindow` always draws a marker at, so
 * the WBS visualizer's "today" line reads as "if this project's first milestone started now."
 */

import { addDays, formatDate, nextWorkday } from './cascade.js'
import { todayUTC } from './ganttLayout.js'

/**
 * @param {Array} wbsRows  wbs_templates rows: { id, deliverable_type_name, subtitle, default_duration,
 *   predecessor_ids, is_wait, phase, sort_order }
 * @returns {{ rows: Array, deps: Array }} normalized rows (id, name, earliest_start, latest_finish,
 *   duration_days, isWait, phase) ready for GanttTimeline, and the resolved dependency edges.
 */
export function simulateWbsSchedule(wbsRows) {
  const byId = new Map((wbsRows || []).map(r => [r.id, r]))
  const anchor = todayUTC()
  const finishCache = new Map()   // id -> Date (finish)
  const startCache = new Map()    // id -> Date (start)
  const visiting = new Set()

  function finishOf(id) {
    if (finishCache.has(id)) return finishCache.get(id)
    if (visiting.has(id)) return anchor   // cycle guard — never hang on bad data
    visiting.add(id)
    const row = byId.get(id)
    let start = anchor
    if (row) {
      const preds = (row.predecessor_ids || []).filter(pid => byId.has(pid))
      if (preds.length) {
        let latest = null
        preds.forEach(pid => { const f = finishOf(pid); if (!latest || f > latest) latest = f })
        if (latest) start = nextWorkday(addDays(latest, 1))
      }
    }
    const duration = row ? (Number(row.default_duration) || 0) : 0
    const finish = addDays(start, duration)
    startCache.set(id, start)
    finishCache.set(id, finish)
    visiting.delete(id)
    return finish
  }

  wbsRows.forEach(r => finishOf(r.id))

  // `...r` keeps every original WBS field (deliverable_type_airtable_id, sort_order,
  // predecessor_ids, is_wait, subtitle, …) so a caller's label-column rendering can read the raw
  // row exactly as before; the fields below are what GanttTimeline actually draws from.
  const rows = wbsRows.map(r => ({
    ...r,
    name: (r.deliverable_type_name || '(untitled)') + (r.subtitle ? ' — ' + r.subtitle : ''),
    earliest_start: formatDate(startCache.get(r.id)),
    latest_finish: formatDate(finishCache.get(r.id)),
    duration_days: Number(r.default_duration) || 0,
    isWait: !!r.is_wait,
  }))

  const deps = []
  wbsRows.forEach(r => {
    (r.predecessor_ids || []).forEach(pid => { if (byId.has(pid)) deps.push({ milestone_id: r.id, predecessor_id: pid }) })
  })

  return { rows, deps }
}

// Mirrors api/_scheduleTimeline.js's own constant/formula — keep both in sync if either changes.
// (Not imported directly: that file is a server-side Vercel helper, this one runs in the browser.)
const CONSTRUCTION_KICKOFF_TYPE_ID = 'reccIbrEt0PfXr731'
const FLOW_TIME_FACTOR = 4.2
const PROJECT_BUFFER_FRACTION = 0.5

/**
 * The template's OWN implied project buffer — same formula api/_scheduleTimeline.js#computeProjectTimeline
 * uses for a real project's Gantt-toolbar readout (0.5 × chain touch-time × 4.2 flow-time factor), but
 * walked over the TEMPLATE's "fullest plausible chain" (every predecessor CANDIDATE treated as real —
 * this file's own simulateWbsSchedule doctrine) rather than one project's actual scheduled milestones.
 * Deliberately a worst-case CEILING, not a promise for any real project: optional phases (Discretionary
 * Approvals, Interior Architecture, …) all count as if they always apply, which no single real chain does.
 *
 * @param {Array} wbsRows
 * @returns {{ chainDaysTouch: number, bufferDays: number, found: boolean }} found=false when no template
 *   row is typed as Construction Kick-Off yet — nothing to measure against.
 */
export function computeTemplateBuffer(wbsRows) {
  const byId = new Map((wbsRows || []).map(r => [r.id, r]))
  const target = (wbsRows || []).find(r => r.deliverable_type_airtable_id === CONSTRUCTION_KICKOFF_TYPE_ID)
  if (!target) return { chainDaysTouch: 0, bufferDays: 0, found: false }

  // Longest path (by touch-time days + any feeding-buffer gap_days) ending at `target`, walking
  // predecessor candidates backward — same shape as _scheduleTimeline.js's own longestTo, just over
  // WBS row ids/candidates instead of a real project's schedule_dependencies.
  const memo = new Map()
  const visiting = new Set()
  function longestTo(id) {
    if (memo.has(id)) return memo.get(id)
    if (visiting.has(id)) return 0
    visiting.add(id)
    const r = byId.get(id)
    const own = r ? ((Number(r.default_duration) || 0) + (Number(r.gap_days) || 0)) : 0
    let best = own
    const preds = (r && r.predecessor_ids || []).filter(pid => byId.has(pid))
    preds.forEach(pid => { const total = longestTo(pid) + own; if (total > best) best = total })
    visiting.delete(id)
    memo.set(id, best)
    return best
  }

  const chainDaysTouch = longestTo(target.id)
  const bufferDays = PROJECT_BUFFER_FRACTION * chainDaysTouch * FLOW_TIME_FACTOR
  return { chainDaysTouch, bufferDays, found: true }
}
